import React from 'react'
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  redirect,
} from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { isEmpty } from 'lodash'

import { RootStore } from '~/store/RootStore'
import { Constants } from '~/utils/Constants'
import { TokenManager } from '~/utils/TokenManager'
import { appendAdobeAnalytics } from '~/utils/analytics'

import GlobalStyle from '~/components/GlobalStyle'
import Loading from '~/components/loading'

type IIndexSearchParam = {
  token?: string
  returnTo?: string | undefined
  redirect?: string | undefined
  path?: string
  triggeredBy?: string
  addOns?: string
}

interface MyRouterContext {
  store: RootStore
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  head: () => ({
    meta: [
      {
        title: Constants.WHOLESALERNAME + 'Self Service Portal',
      },
    ],
  }),
  validateSearch: (
    search: Record<string, unknown>
  ): IIndexSearchParam | undefined => {
    if (search.token) {
      // Make sure to clear the token manager first
      TokenManager.clearToken()
      return search as IIndexSearchParam
    }
    return undefined
  },
  beforeLoad: async ({ context, location }) => {
    // append analytics
    if (Constants.WHOLESALERCODE.toLowerCase() === 'oe') {
      appendAdobeAnalytics()
    }

    const { app, account } = context.store
    const search = location.search as IIndexSearchParam

    if (location.pathname !== '/login') {
      const returnTo = search.returnTo
      const defaultReturnTo = Constants.LOGINURL

      const returnToUrl: string = returnTo ? returnTo : defaultReturnTo

      // save the return url in session storage
      sessionStorage.setItem('anex-oss-rto', returnToUrl || '/')

      //Check URL Param if we have token
      if (search && search.token) {
        await app.refreshToken(search.token)
      }

      // If we don't have a token in URL parameter
      // It might means the user just refreshed the browser
      // Check the stored token
      if (!search || !search.token) {
        const storedToken = app.token || TokenManager.getToken()

        if (storedToken) {
          if (TokenManager.validateToken(storedToken)) {
            await app.refreshToken(storedToken)
          } else {
            if (Constants.WHOLESALERCODE.toLowerCase() === 'oe') {
              return window.location.replace(returnToUrl)
            }
            throw redirect({ to: returnToUrl })
          }
        } else {
          if (Constants.WHOLESALERCODE.toLowerCase() === 'oe') {
            return window.location.replace(returnToUrl)
          }
          throw redirect({ to: returnToUrl })
        }
      }

      // should continue here if everything is good
      if (isEmpty(account.profile)) {
        await account.loadProfile()
      }
      if (account.services.length === 0) {
        await account.loadServices()
      }
    }
  },
  loader: ({ context }) => {
    const isAuthenticated = context?.store?.app?.isAuthenticated

    return isAuthenticated
  },
  pendingComponent: () => <Loading />,
  component: RootComponent,
})

function RootComponent() {
  return (
    <React.Fragment>
      <HeadContent />
      <GlobalStyle />
      <Outlet />

      <TanStackRouterDevtools position="bottom-right" initialIsOpen={false} />
    </React.Fragment>
  )
}
